/**
 * Firestore コレクション 差分同期キャッシュ基盤 (v3.3.x)。
 *
 * 目的:
 *   - リスト系コレクションの全件取得コストを 90% 以上削減
 *   - `_meta/{collectionName}` 集約ドキュメントを 1 read で確認
 *   - lastModifiedAt / deletedCount に差があれば差分取得 / 全件再取得
 *
 * ストレージ: localStorage (1 コレクション数千件程度なら容量的に十分)。
 * 容量が逼迫する場合は IndexedDB への移行を検討。
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  Timestamp,
  where,
  type DocumentData,
  type Firestore,
  type QueryConstraint,
} from 'firebase/firestore';

interface MetaSnapshot {
  lastModifiedAtMs: number;
  count: number;
  deletedCount: number;
}

interface CacheRecord<T> {
  /** Cache schema バージョン。schema を変える時は CACHE_VERSION を上げる */
  v: number;
  /** 最後にサーバーから取得した時刻 (UTC ms) */
  fetchedAtMs: number;
  /** 取得時点のメタスナップショット */
  meta: MetaSnapshot;
  /** ドキュメント本体 (Firestore Timestamp は ISO 文字列にシリアライズして保存) */
  items: T[];
}

const CACHE_VERSION = 1;

function storageKey(collectionName: string): string {
  return `caudGoEnHub:cache:v${CACHE_VERSION}:${collectionName}`;
}

/**
 * Firestore Timestamp を含むオブジェクトを localStorage 用にシリアライズ。
 * 復元時のために `__ts` マーカーを付ける。
 */
function serialize(value: unknown): unknown {
  if (value == null) return value;
  if (value instanceof Timestamp) {
    return { __ts: value.toMillis() };
  }
  if (Array.isArray(value)) return value.map(serialize);
  if (typeof value === 'object') {
    const obj: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      obj[k] = serialize(v);
    }
    return obj;
  }
  return value;
}

function deserialize(value: unknown): unknown {
  if (value == null) return value;
  if (Array.isArray(value)) return value.map(deserialize);
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    if (typeof v.__ts === 'number') {
      return Timestamp.fromMillis(v.__ts);
    }
    const obj: Record<string, unknown> = {};
    for (const [k, vv] of Object.entries(v)) {
      obj[k] = deserialize(vv);
    }
    return obj;
  }
  return value;
}

function readCache<T>(collectionName: string): CacheRecord<T> | null {
  try {
    const raw = localStorage.getItem(storageKey(collectionName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheRecord<unknown>;
    if (parsed.v !== CACHE_VERSION) return null;
    return {
      ...parsed,
      items: parsed.items.map((it) => deserialize(it)) as T[],
    } as CacheRecord<T>;
  } catch {
    return null;
  }
}

function writeCache<T>(collectionName: string, record: CacheRecord<T>): void {
  try {
    const payload: CacheRecord<unknown> = {
      ...record,
      items: record.items.map((it) => serialize(it)),
    };
    localStorage.setItem(storageKey(collectionName), JSON.stringify(payload));
  } catch (err) {
    // QuotaExceededError 等。安全のためキャッシュを削除して諦める。
    console.warn('[collectionCache] write failed, clearing:', err);
    try {
      localStorage.removeItem(storageKey(collectionName));
    } catch {
      /* noop */
    }
  }
}

export function clearCollectionCache(collectionName: string): void {
  try {
    localStorage.removeItem(storageKey(collectionName));
  } catch {
    /* noop */
  }
}

/**
 * 楽観的更新: ローカルキャッシュから id 指定で 1 件を除去する。
 *
 * 削除時の `deletedCount` 更新はサーバー側 Trigger (`onMailContactWritten` 等) が
 * 非同期かつ数秒遅延で行うため、削除直後の再取得では meta が「変化なし」と判定され
 * キャッシュ (= 削除済みの行を含む) がそのまま返ってしまう。
 * クライアント自身が起こした削除は結果が確定しているので、ここでキャッシュから直接
 * 取り除いて即時に一覧へ反映する。meta は据え置きとし、後続で Trigger が
 * deletedCount を更新したタイミングの全件再取得で自然に整合が取れる (自己修復)。
 */
export function removeItemFromCollectionCache(
  collectionName: string,
  id: string,
): void {
  try {
    const cached = readCache<{ id: string }>(collectionName);
    if (!cached) return;
    const next = cached.items.filter((it) => it.id !== id);
    if (next.length === cached.items.length) return; // 該当なし → 触らない
    writeCache(collectionName, { ...cached, items: next });
  } catch {
    /* noop */
  }
}

/** UI 用: 最終同期時刻 (UTC ms) と総件数を返す。キャッシュが無ければ null。 */
export function getCollectionCacheInfo(collectionName: string): {
  fetchedAtMs: number;
  count: number;
} | null {
  try {
    const raw = localStorage.getItem(storageKey(collectionName));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheRecord<unknown>;
    if (parsed.v !== CACHE_VERSION) return null;
    return {
      fetchedAtMs: parsed.fetchedAtMs,
      count: parsed.items.length,
    };
  } catch {
    return null;
  }
}

async function fetchMeta(
  db: Firestore,
  collectionName: string,
): Promise<MetaSnapshot | null> {
  const snap = await getDoc(doc(db, '_meta', collectionName));
  if (!snap.exists()) return null;
  const data = snap.data();
  const lastModifiedAt = data.lastModifiedAt;
  return {
    lastModifiedAtMs:
      lastModifiedAt instanceof Timestamp ? lastModifiedAt.toMillis() : 0,
    count: typeof data.count === 'number' ? data.count : 0,
    deletedCount:
      typeof data.deletedCount === 'number' ? data.deletedCount : 0,
  };
}

export interface FetchWithCacheOptions {
  /**
   * 追加クエリ制約 (where 句など)。クエリが変わるとキャッシュキーも変える必要があるため、
   * キャッシュを使えるのは「全件取得」相当のクエリのみを推奨。
   * orderBy('updatedAt', 'desc') は内部で常に付与されるのでここでは指定しない。
   */
  extraConstraints?: QueryConstraint[];
  /** 強制リフレッシュ (キャッシュを無視して全件取得) */
  forceFullRefetch?: boolean;
}

export interface FetchWithCacheResult<T> {
  items: T[];
  /** このフェッチで読み取った Firestore ドキュメント数 (デバッグ用) */
  readsThisFetch: number;
  /** メタ情報 (キャッシュ表示用) */
  meta: MetaSnapshot | null;
  /** キャッシュからの返却か */
  fromCache: boolean;
  /** 最後に同期した時刻 (UTC ms) */
  fetchedAtMs: number;
}

/**
 * 差分同期キャッシュ付きで Firestore コレクションを取得。
 *
 * 動作:
 *   1. メタドキュメント 1 read で集約情報取得
 *   2. キャッシュがあり meta が完全一致 → 0 read で返す
 *   3. キャッシュがあり deletedCount のみ一致 (= 削除なし) →
 *      `where('updatedAt', '>', cacheLastModifiedAt)` で差分取得しマージ
 *   4. それ以外 → 全件取得
 */
export async function fetchCollectionWithCache<T extends DocumentData>(
  db: Firestore,
  collectionName: string,
  toRecord: (id: string, data: DocumentData) => T,
  options?: FetchWithCacheOptions,
): Promise<FetchWithCacheResult<T>> {
  const meta = await fetchMeta(db, collectionName);
  const cached = options?.forceFullRefetch ? null : readCache<T>(collectionName);

  // メタが取得できない (まだ trigger が走ったことがない) → 全件取得して初期化
  if (!meta) {
    const full = await fullFetch(db, collectionName, toRecord, options);
    return {
      ...full,
      meta: null,
      fromCache: false,
    };
  }

  // キャッシュ完全一致 → 0 read
  if (
    cached &&
    cached.meta.lastModifiedAtMs === meta.lastModifiedAtMs &&
    cached.meta.deletedCount === meta.deletedCount
  ) {
    return {
      items: cached.items,
      readsThisFetch: 1, // meta 1 read のみ
      meta,
      fromCache: true,
      fetchedAtMs: cached.fetchedAtMs,
    };
  }

  // deletedCount が変わった → 全件再取得
  if (cached && cached.meta.deletedCount !== meta.deletedCount) {
    const full = await fullFetch(db, collectionName, toRecord, options);
    return { ...full, meta, fromCache: false };
  }

  // lastModifiedAt のみ違う + キャッシュあり → 差分取得
  if (cached) {
    const cutoff = Timestamp.fromMillis(cached.meta.lastModifiedAtMs);
    const q = query(
      collection(db, collectionName),
      ...(options?.extraConstraints ?? []),
      where('updatedAt', '>', cutoff),
      orderBy('updatedAt', 'desc'),
    );
    const snap = await getDocs(q);
    const changed = snap.docs.map((d) => toRecord(d.id, d.data()));

    // マージ: 既存キャッシュから changed.id を除き、changed を頭に追加
    const changedIds = new Set(changed.map((c) => (c as unknown as { id: string }).id));
    const merged = [
      ...changed,
      ...cached.items.filter(
        (c) => !changedIds.has((c as unknown as { id: string }).id),
      ),
    ];

    const record: CacheRecord<T> = {
      v: CACHE_VERSION,
      fetchedAtMs: Date.now(),
      meta,
      items: merged,
    };
    writeCache(collectionName, record);

    return {
      items: merged,
      readsThisFetch: 1 + snap.size, // meta 1 + 差分
      meta,
      fromCache: false,
      fetchedAtMs: record.fetchedAtMs,
    };
  }

  // キャッシュなし → 全件取得
  const full = await fullFetch(db, collectionName, toRecord, options);
  return { ...full, meta, fromCache: false };
}

async function fullFetch<T extends DocumentData>(
  db: Firestore,
  collectionName: string,
  toRecord: (id: string, data: DocumentData) => T,
  options?: FetchWithCacheOptions,
): Promise<{ items: T[]; readsThisFetch: number; fetchedAtMs: number }> {
  const q = query(
    collection(db, collectionName),
    ...(options?.extraConstraints ?? []),
    orderBy('updatedAt', 'desc'),
  );
  const snap = await getDocs(q);
  const items = snap.docs.map((d) => toRecord(d.id, d.data()));

  // メタを取り直して保存 (full fetch は時間がかかるので新鮮なメタが必要)
  const meta = await fetchMeta(db, collectionName);
  if (meta) {
    const record: CacheRecord<T> = {
      v: CACHE_VERSION,
      fetchedAtMs: Date.now(),
      meta,
      items,
    };
    writeCache(collectionName, record);
  }

  return {
    items,
    readsThisFetch: snap.size + 1, // meta 1 read 含む
    fetchedAtMs: Date.now(),
  };
}
